import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { DocumentListItem, DocumentDetail } from '../DocumentAPI';

/**
 * DMS Right Panel Types
 */
export type DMSPanelMode = 'closed' | 'document_editor' | 'document_viewer' | 'document_creator';

/**
 * Version Status Types
 */
export type VersionStatus = 'draft' | 'review' | 'approved' | 'final' | 'archived';

/**
 * Version Metadata Interface
 */
export interface VersionMetadata {
  version_number: string; // e.g., "v1.0", "v1.1", "v2.0"
  version_name?: string; // Optional custom name
  change_description: string;
  author_id: string;
  author_name: string;
  timestamp: string;
  approval_status: VersionStatus;
  source: 'create' | 'save' | 'import' | 'rollback' | 'auto-save';
  is_major_version: boolean; // true for v1.0, v2.0; false for v1.1, v1.2
}

/**
 * New Document State Interface
 */
export interface NewDocumentState {
  title: string;
  client_id: string | null;
  matter_id: string | null;
  document_type: string | null;
  template_id: string | null;
  editor_type: 'edgedocs' | 'msword';
  tags: string[];
  content: string;
  initial_version: VersionMetadata;
  is_creating: boolean;
  has_unsaved_changes: boolean;
  last_auto_save: Date | null;
}

export interface DMSPanelState {
  // Panel state
  isPanelOpen: boolean;
  isPanelPinned: boolean;
  panelMode: DMSPanelMode;
  panelWidth: number;
  customPanelWidth: number | null;
  
  // Document state
  currentDocument: DocumentListItem | DocumentDetail | null;
  isCreatingNewDocument: boolean;
  documentType: string | null;
  clientId: string | null;
  matterId: string | null;
  
  // New document creation state
  newDocumentState: NewDocumentState;
  
  // Version management state
  currentVersion: VersionMetadata | null;
  versionHistory: VersionMetadata[];
  isVersionHistoryLoading: boolean;
  
  // Animation state
  isAnimating: boolean;
  isResizing: boolean;
  
  // UI state
  showFloatingLauncher: boolean;
  headerHeight: number;
}

export interface DMSPanelActions {
  // Panel actions
  openPanel: (mode: DMSPanelMode) => void;
  closePanel: () => void;
  togglePanel: () => void;
  setPanelPinned: (pinned: boolean) => void;
  setPanelWidth: (width: number) => void;
  setCustomPanelWidth: (width: number | null) => void;
  
  // Document actions
  openDocumentEditor: (document: DocumentListItem | DocumentDetail) => void;
  openDocumentViewer: (document: DocumentListItem | DocumentDetail) => void;
  openDocumentCreator: (clientId?: string, matterId?: string, documentType?: string) => void;
  setCurrentDocument: (document: DocumentListItem | DocumentDetail | null) => void;
  
  // New document creation actions
  initializeNewDocument: (metadata?: Partial<NewDocumentState>) => void;
  updateNewDocumentMetadata: (updates: Partial<NewDocumentState>) => void;
  updateNewDocumentContent: (content: string) => void;
  saveNewDocument: () => Promise<string>; // Returns document ID
  resetNewDocument: () => void;
  
  // Version management actions
  loadVersionHistory: (documentId: string) => Promise<void>;
  createNewVersion: (documentId: string, metadata: Partial<VersionMetadata>) => Promise<void>;
  switchToVersion: (documentId: string, versionNumber: string) => Promise<void>;
  compareVersions: (version1: string, version2: string) => Promise<any>;
  rollbackToVersion: (documentId: string, versionNumber: string) => Promise<void>;
  exportVersion: (documentId: string, versionNumber: string, format: 'pdf' | 'docx') => Promise<Blob>;
  
  // Animation actions
  setAnimating: (animating: boolean) => void;
  setResizing: (resizing: boolean) => void;
  
  // UI actions
  setShowFloatingLauncher: (show: boolean) => void;
  setHeaderHeight: (height: number) => void;
}

export type DMSRightPanelStore = DMSPanelState & DMSPanelActions;

// Default panel widths (full manual control)
const DEFAULT_PANEL_WIDTH = 40; // Percentage of screen
const MIN_PANEL_WIDTH = 25; // Minimum percentage
const MAX_PANEL_WIDTH = 96; // Maximum percentage - can expand to sidebar edge

// Default new document state
const defaultNewDocumentState: NewDocumentState = {
  title: '',
  client_id: null,
  matter_id: null,
  document_type: null,
  template_id: null,
  editor_type: 'edgedocs',
  tags: [],
  content: '',
  initial_version: {
    version_number: 'v1.0',
    change_description: 'Initial document creation',
    author_id: '',
    author_name: '',
    timestamp: new Date().toISOString(),
    approval_status: 'draft',
    source: 'create',
    is_major_version: true,
  },
  is_creating: false,
  has_unsaved_changes: false,
  last_auto_save: null,
};

export const useDMSRightPanelStore = create<DMSRightPanelStore>()(
  persist(
    (set, get) => ({
      // Initial state
      isPanelOpen: false,
      isPanelPinned: false,
      panelMode: 'closed',
      panelWidth: DEFAULT_PANEL_WIDTH,
      customPanelWidth: null,
      
      currentDocument: null,
      isCreatingNewDocument: false,
      documentType: null,
      clientId: null,
      matterId: null,
      
      newDocumentState: defaultNewDocumentState,
      
      currentVersion: null,
      versionHistory: [],
      isVersionHistoryLoading: false,
      
      isAnimating: false,
      isResizing: false,
      
      showFloatingLauncher: true,
      headerHeight: 64,
      
      // Panel actions
      openPanel: (mode: DMSPanelMode) => {
        set({ 
          isPanelOpen: true, 
          panelMode: mode,
          isAnimating: true,
          showFloatingLauncher: false
        });
        
        // Clear animation state after transition
        setTimeout(() => {
          set({ isAnimating: false });
        }, 300);
      },
      
      closePanel: () => {
        set({ 
          isAnimating: true,
          showFloatingLauncher: true
        });
        
        setTimeout(() => {
          set({ 
            isPanelOpen: false, 
            panelMode: 'closed',
            isAnimating: false,
            currentDocument: null,
            isCreatingNewDocument: false,
            documentType: null,
            clientId: null,
            matterId: null,
            newDocumentState: defaultNewDocumentState,
            currentVersion: null,
            versionHistory: []
          });
        }, 300);
      },
      
      togglePanel: () => {
        const { isPanelOpen } = get();
        if (isPanelOpen) {
          get().closePanel();
        } else {
          get().openPanel('document_creator');
        }
      },
      
      setPanelPinned: (pinned: boolean) => {
        set({ isPanelPinned: pinned });
      },
      
      setPanelWidth: (width: number) => {
        const clampedWidth = Math.max(MIN_PANEL_WIDTH, Math.min(MAX_PANEL_WIDTH, width));
        set({ panelWidth: clampedWidth });
      },
      
      setCustomPanelWidth: (width: number | null) => {
        set({ customPanelWidth: width });
      },
      
      // Document actions
      openDocumentEditor: (document: DocumentListItem | DocumentDetail) => {
        set({
          currentDocument: document,
          isCreatingNewDocument: false,
          documentType: document.type || null,
          clientId: document.client_id || null,
          matterId: document.matter_id || null
        });
        get().openPanel('document_editor');
      },
      
      openDocumentViewer: (document: DocumentListItem | DocumentDetail) => {
        set({
          currentDocument: document,
          isCreatingNewDocument: false,
          documentType: document.type || null,
          clientId: document.client_id || null,
          matterId: document.matter_id || null
        });
        get().openPanel('document_viewer');
      },
      
      openDocumentCreator: (clientId?: string, matterId?: string, documentType?: string) => {
        set({
          currentDocument: null,
          isCreatingNewDocument: true,
          documentType: documentType || null,
          clientId: clientId || null,
          matterId: matterId || null
        });
        get().openPanel('document_creator');
      },
      
      setCurrentDocument: (document: DocumentListItem | DocumentDetail | null) => {
        set({ currentDocument: document });
      },
      
      // New document creation actions
      initializeNewDocument: (metadata = {}) => {
        // Get user information from Keycloak token instead of localStorage
        let authorId = '';
        let authorName = 'Unknown User';
        
        // Try to get user info from Keycloak token
        try {
          const token = localStorage.getItem('webelieve_access_token');
          if (token) {
            const payload = JSON.parse(atob(token.split('.')[1]));
            authorId = payload.sub || '';
            authorName = payload.name || payload.preferred_username || payload.email || 'Unknown User';
          }
        } catch (error) {
          console.warn('Could not extract user info from token:', error);
        }
        
        const initialState: NewDocumentState = {
          ...defaultNewDocumentState,
          ...metadata,
          initial_version: {
            ...defaultNewDocumentState.initial_version,
            author_id: authorId,
            author_name: authorName,
            ...metadata.initial_version,
          }
        };
        
        set({ 
          newDocumentState: initialState,
          isCreatingNewDocument: true
        });
      },
      
      updateNewDocumentMetadata: (updates) => {
        set((state) => ({
          newDocumentState: {
            ...state.newDocumentState,
            ...updates,
            has_unsaved_changes: true
          }
        }));
      },
      
      updateNewDocumentContent: (content) => {
        set((state) => ({
          newDocumentState: {
            ...state.newDocumentState,
            content,
            has_unsaved_changes: true
          }
        }));
      },
      
      saveNewDocument: async () => {
        const { newDocumentState } = get();
        
        if (!newDocumentState.title || !newDocumentState.client_id || !newDocumentState.matter_id) {
          throw new Error('Missing required fields: title, client, and matter are required');
        }
        
        set((state) => ({
          newDocumentState: {
            ...state.newDocumentState,
            is_creating: true
          }
        }));
        
        try {
          // Get authentication token
          const token = localStorage.getItem('webelieve_access_token');
          if (!token) {
            throw new Error('No authentication token available');
          }
          
          // Prepare document data for API with proper initial content
          const initialLexicalContent = `{"root":{"children":[{"children":[{"detail":0,"format":0,"mode":"normal","style":"","text":"${newDocumentState.title}","type":"text","version":1}],"direction":"ltr","format":"","indent":0,"type":"paragraph","version":1}],"direction":"ltr","format":"","indent":0,"type":"root","version":1}}`;
          
          const isMsWord = newDocumentState.editor_type === 'msword';

          const documentData = {
            title: newDocumentState.title,
            description: `Document created via DMS: ${newDocumentState.title}`,
            client_id: newDocumentState.client_id,
            matter_id: newDocumentState.matter_id,
            document_type: newDocumentState.document_type || 'general',
            template_id: newDocumentState.template_id || undefined,
            editor_type: newDocumentState.editor_type || 'edgedocs',
            tags: newDocumentState.tags,
            content: isMsWord ? undefined : (newDocumentState.content || initialLexicalContent),
            // AST only for EdgeDocs — Word documents are DOCX-authoritative
            ...(!isMsWord ? {
              ast: {
                blocks: [
                  { type: 'Paragraph', id: crypto.randomUUID(), children: [{ text: newDocumentState.title }] }
                ]
              },
              ast_version: 'v1',
            } : {}),
            ai_processing: true,
            metadata: {
              version: newDocumentState.initial_version.version_number,
              author: newDocumentState.initial_version.author_name,
              created_via: 'dms_creator'
            }
          };
          
          console.log('Creating new document via API:', documentData);
          
          // Make API call to create document
          const response = await fetch('/api/v1/dms/documents', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(documentData)
          });
          
          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Document creation failed: ${response.statusText}. ${errorText}`);
          }
          
          const newDocument = await response.json();
          console.log('✅ [dmsRightPanelStore] Document created successfully:', {
            newDocument,
            documentTitle: newDocument.title || newDocument.documents?.[0]?.title,
            hasTitle: !!(newDocument.title || newDocument.documents?.[0]?.title),
            fullResponse: newDocument
          });
          
          set((state) => ({
            newDocumentState: {
              ...state.newDocumentState,
              is_creating: false,
              has_unsaved_changes: false
            }
          }));
          
          // Extract document ID from the response structure
          const document = newDocument.documents?.[0] || newDocument;
          
          console.log('📄 [dmsRightPanelStore] Extracted document:', {
            documentId: document.id || document.document_id,
            documentTitle: document.title,
            hasTitle: !!document.title
          });
          return document.document_id || document.id;
        } catch (error) {
          console.error('Error creating document:', error);
          set((state) => ({
            newDocumentState: {
              ...state.newDocumentState,
              is_creating: false
            }
          }));
          throw error;
        }
      },
      
      resetNewDocument: () => {
        set({ newDocumentState: defaultNewDocumentState });
      },
      
      // Version management actions
      loadVersionHistory: async (documentId: string) => {
        set({ isVersionHistoryLoading: true });
        
        try {
          // TODO: Implement actual API call to load version history
          // This will be implemented in Checkpoint 2.3
          console.log('Loading version history for document:', documentId);
          
          // Simulate API call
          await new Promise(resolve => setTimeout(resolve, 500));
          
          // Mock version history
          const mockVersionHistory: VersionMetadata[] = [
            {
              version_number: 'v1.0',
              change_description: 'Initial document creation',
              author_id: 'user1',
              author_name: 'John Doe',
              timestamp: new Date().toISOString(),
              approval_status: 'draft',
              source: 'create',
              is_major_version: true,
            }
          ];
          
          set({
            versionHistory: mockVersionHistory,
            currentVersion: mockVersionHistory[0],
            isVersionHistoryLoading: false
          });
        } catch (error) {
          set({ isVersionHistoryLoading: false });
          throw error;
        }
      },
      
      createNewVersion: async (documentId: string, metadata: Partial<VersionMetadata>) => {
        // TODO: Implement actual API call to create new version
        // This will be implemented in Checkpoint 2.3
        console.log('Creating new version for document:', documentId, metadata);
        
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Update version history
        const { versionHistory } = get();
        const newVersion: VersionMetadata = {
          version_number: 'v1.1', // TODO: Calculate next version number
          change_description: metadata.change_description || 'Document updated',
          author_id: metadata.author_id || 'user1',
          author_name: metadata.author_name || 'John Doe',
          timestamp: new Date().toISOString(),
          approval_status: metadata.approval_status || 'draft',
          source: metadata.source || 'save',
          is_major_version: metadata.is_major_version || false,
        };
        
        set({
          versionHistory: [...versionHistory, newVersion],
          currentVersion: newVersion
        });
      },
      
      switchToVersion: async (documentId: string, versionNumber: string) => {
        // TODO: Implement actual API call to load specific version
        // This will be implemented in Checkpoint 2.3
        console.log('Switching to version:', versionNumber, 'for document:', documentId);
        
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 300));
        
        const { versionHistory } = get();
        const targetVersion = versionHistory.find(v => v.version_number === versionNumber);
        
        if (targetVersion) {
          set({ currentVersion: targetVersion });
        }
      },
      
      compareVersions: async (version1: string, version2: string) => {
        // TODO: Implement actual API call to compare versions
        // This will be implemented in Checkpoint 3.2
        console.log('Comparing versions:', version1, 'and', version2);
        
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Return mock diff data
        return {
          version1,
          version2,
          differences: [
            { type: 'added', content: 'New content added', position: 10 },
            { type: 'removed', content: 'Old content removed', position: 25 }
          ]
        };
      },
      
      rollbackToVersion: async (documentId: string, versionNumber: string) => {
        // TODO: Implement actual API call to rollback to version
        // This will be implemented in Checkpoint 3.3
        console.log('Rolling back to version:', versionNumber, 'for document:', documentId);
        
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Create new version with rolled-back content
        await get().createNewVersion(documentId, {
          change_description: `Rolled back to ${versionNumber}`,
          source: 'rollback'
        });
      },
      
      exportVersion: async (documentId: string, versionNumber: string, format: 'pdf' | 'docx') => {
        // TODO: Implement actual API call to export version
        // This will be implemented in Checkpoint 3.4
        console.log('Exporting version:', versionNumber, 'as', format, 'for document:', documentId);
        
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Return mock blob
        return new Blob(['Mock exported content'], { type: format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
      },
      
      // Animation actions
      setAnimating: (animating: boolean) => {
        set({ isAnimating: animating });
      },
      
      setResizing: (resizing: boolean) => {
        set({ isResizing: resizing });
      },
      
      // UI actions
      setShowFloatingLauncher: (show: boolean) => {
        set({ showFloatingLauncher: show });
      },
      
      setHeaderHeight: (height: number) => {
        set({ headerHeight: height });
      }
    }),
    {
      name: 'dms-right-panel-store',
      partialize: (state) => ({
        isPanelPinned: state.isPanelPinned,
        panelWidth: state.panelWidth,
        customPanelWidth: state.customPanelWidth,
        showFloatingLauncher: state.showFloatingLauncher,
        headerHeight: state.headerHeight,
      }),
    }
  )
);

// Utility functions for panel width calculations
export const calculatePanelWidth = (windowWidth: number, panelWidthPercent: number): number => {
  return Math.round((panelWidthPercent / 100) * windowWidth);
};

export const calculateMainContentMargin = (
  windowWidth: number, 
  panelWidthPercent: number, 
  isPanelOpen: boolean
): number => {
  if (!isPanelOpen) return 0;
  return calculatePanelWidth(windowWidth, panelWidthPercent);
};

// Document type constants
export const DOCUMENT_TYPES = [
  'contract',
  'motion',
  'brief',
  'memo',
  'letter',
  'agreement',
  'filing',
  'correspondence',
  'report',
  'other'
] as const;

export type DocumentType = typeof DOCUMENT_TYPES[number];

export const getDocumentTypeLabel = (type: DocumentType): string => {
  const labels: Record<DocumentType, string> = {
    contract: 'Contract',
    motion: 'Motion',
    brief: 'Brief',
    memo: 'Memo',
    letter: 'Letter',
    agreement: 'Agreement',
    filing: 'Filing',
    correspondence: 'Correspondence',
    report: 'Report',
    other: 'Other'
  };
  return labels[type] || type;
}; 